import { useEffect, useRef, useState } from "react";

// ===========================================================================
// BatteryViz — five components that make the Battery Care app feel alive.
//
//   <BatteryGlyph voltage={12.4} />        — animated battery icon, fills
//                                              by voltage (12.0V→empty,
//                                              12.7V→full). 36×16 default.
//   <VoltageDial voltage={12.4} />         — semicircle gauge with needle
//                                              that swings on first render.
//   <ChargingHalo>{children}</ChargingHalo> — wraps a row/card. Adds a
//                                              pulsing cobalt halo behind
//                                              its content.
//   <FleetHealthRing buckets={{...}} />    — donut chart of fleet split,
//                                              animates from empty on mount.
//   <LiveTick value={12.42} unit="V" />    — number that morphs softly
//                                              when value changes (no snap).
//
// All shapes use SEVRIC cooled palette via CSS variables. Respect
// `prefers-reduced-motion` everywhere.
// ===========================================================================

// Voltage → fill fraction. 12.0V counts as 0% (dead-ish for a 12V), 12.7V is
// 100% (full SLA/AGM resting). Anything outside clamps.
function voltageToPct(v) {
  if (typeof v !== "number" || Number.isNaN(v)) return 0;
  const min = 12.0, max = 12.7;
  return Math.max(0, Math.min(1, (v - min) / (max - min)));
}
function voltageBand(v) {
  // Visual color band — healthy / low / critical.
  if (typeof v !== "number" || Number.isNaN(v)) return "unknown";
  if (v >= 12.5) return "healthy";
  if (v >= 12.2) return "low";
  return "critical";
}
const BAND_COLOR = {
  healthy:  "var(--ok)",
  low:      "var(--warn)",
  critical: "var(--danger)",
  unknown:  "var(--text-faint)",
};

// =========================================================================
// 1. BatteryGlyph — animated battery icon with liquid fill.
// =========================================================================
export function BatteryGlyph({
  voltage,
  width = 48,
  height = 22,
  showValue = false,
  className = "",
}) {
  const pct = voltageToPct(voltage);
  const band = voltageBand(voltage);
  const fillColor = BAND_COLOR[band];
  // Animate fill width from 0 to target on first mount + on voltage changes.
  const [w, setW] = useState(0);
  useEffect(() => {
    const t = setTimeout(() => setW(pct), 60); // small delay to ensure transition fires
    return () => clearTimeout(t);
  }, [pct]);

  // Battery body: outer rect (rounded), terminal nub on right, inner fill rect.
  // Coordinates set for a 100×40 viewBox so styling scales cleanly.
  const innerPad = 5;
  const innerW = 80 - innerPad * 2;
  const innerH = 40 - innerPad * 2;
  return (
    <span
      className={className}
      style={{ display: "inline-flex", alignItems: "center", gap: showValue ? 8 : 0 }}
      aria-label={`Battery ${voltage != null ? voltage.toFixed(2) + " volts" : "unknown"}`}
    >
      <svg viewBox="0 0 100 40" width={width} height={height} aria-hidden="true">
        {/* body outline */}
        <rect x="2" y="2" width="80" height="36" rx="4" ry="4"
          fill="none" stroke="var(--text-muted)" strokeWidth="2"/>
        {/* terminal nub */}
        <rect x="83" y="12" width="9" height="16" rx="2" ry="2"
          fill="var(--text-muted)"/>
        {/* fill — animated width */}
        <rect
          x={2 + innerPad}
          y={2 + innerPad}
          width={innerW * w}
          height={innerH}
          rx="2"
          fill={fillColor}
          style={{
            transition: "width 700ms cubic-bezier(0.16,1,0.3,1), fill 280ms ease-out",
          }}
        />
        {/* subtle liquid sheen */}
        {w > 0 && (
          <rect
            x={2 + innerPad}
            y={2 + innerPad}
            width={innerW * w}
            height={innerH * 0.45}
            rx="2"
            fill="rgba(255,255,255,0.18)"
            style={{ transition: "width 700ms cubic-bezier(0.16,1,0.3,1)" }}
          />
        )}
      </svg>
      {showValue && (
        <LiveTick value={voltage} unit="V" precision={2} style={{ fontSize: "0.95em" }} />
      )}
    </span>
  );
}

// =========================================================================
// 2. VoltageDial — semicircle gauge. Needle swings from 0 to target on mount.
// =========================================================================
export function VoltageDial({
  voltage,
  size = 180,
  min = 11.0,
  max = 13.0,
}) {
  // Map voltage to angle: -90deg (left, min) → 90deg (right, max).
  const target = typeof voltage === "number" && !Number.isNaN(voltage)
    ? Math.max(0, Math.min(1, (voltage - min) / (max - min)))
    : 0;
  const targetAngle = -90 + target * 180;
  const [angle, setAngle] = useState(-90);
  useEffect(() => {
    const t = setTimeout(() => setAngle(targetAngle), 60);
    return () => clearTimeout(t);
  }, [targetAngle]);

  const band = voltageBand(voltage);
  const arcColor = BAND_COLOR[band];

  // SVG geometry — viewBox 200×120. Arc center at (100, 100), radius 80.
  const cx = 100, cy = 100, r = 80;
  // Background arc — full semicircle.
  const polar = (deg) => {
    const rad = (deg * Math.PI) / 180;
    return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
  };
  const a = polar(180); // left end (12 o'clock - 90 = pi)
  const b = polar(0);   // right end
  const bgArc = `M ${a.x} ${a.y} A ${r} ${r} 0 0 1 ${b.x} ${b.y}`;
  // Filled arc up to needle angle (-90 to +angle).
  // Convert needle angle (centered at 12 o'clock, -90=left, +90=right) to SVG.
  const needleSvgDeg = angle + 180; // -90→90 maps to 90→270
  const needleEnd = polar(needleSvgDeg);
  const largeArc = (needleSvgDeg - 180) > 180 ? 1 : 0;
  const fillArc = `M ${a.x} ${a.y} A ${r} ${r} 0 ${largeArc} 1 ${needleEnd.x} ${needleEnd.y}`;

  return (
    <svg viewBox="0 0 200 120" width={size} height={size * 0.6} role="img"
      aria-label={`Voltage gauge: ${voltage != null ? voltage.toFixed(2) + " volts" : "unknown"}`}>
      {/* Tick marks */}
      {[11.0, 11.5, 12.0, 12.5, 13.0].map((tv) => {
        const t = (tv - min) / (max - min);
        const d = -90 + t * 180;
        const p1 = polar(d + 180);
        const inner = { x: cx + (r - 8) * Math.cos(((d + 180) * Math.PI) / 180),
                        y: cy + (r - 8) * Math.sin(((d + 180) * Math.PI) / 180) };
        return (
          <line key={tv} x1={p1.x} y1={p1.y} x2={inner.x} y2={inner.y}
            stroke="var(--line-strong)" strokeWidth="1.5" strokeLinecap="round"/>
        );
      })}
      {/* Background arc */}
      <path d={bgArc} fill="none" stroke="var(--bg-deep)" strokeWidth="10" strokeLinecap="round"/>
      {/* Filled arc */}
      <path d={fillArc} fill="none" stroke={arcColor} strokeWidth="10" strokeLinecap="round"
        style={{ transition: "stroke 280ms ease-out" }}/>
      {/* Needle */}
      <g style={{ transform: `rotate(${angle + 90}deg)`, transformOrigin: `${cx}px ${cy}px`,
                  transition: "transform 1100ms cubic-bezier(0.34, 1.56, 0.64, 1)" }}>
        <line x1={cx} y1={cy} x2={cx} y2={cy - r + 6}
          stroke="var(--text)" strokeWidth="2.5" strokeLinecap="round"/>
        <circle cx={cx} cy={cy} r="6" fill="var(--text)"/>
        <circle cx={cx} cy={cy} r="2" fill="var(--bg)"/>
      </g>
      {/* Center label */}
      <text x={cx} y={cy + 22} textAnchor="middle"
        style={{ font: "500 22px 'Crimson Pro', serif", fill: "var(--text)", letterSpacing: "-0.02em" }}>
        {typeof voltage === "number" ? voltage.toFixed(2) : "—"}
        <tspan style={{ fontSize: 12, fill: "var(--text-muted)" }}> V</tspan>
      </text>
    </svg>
  );
}

// =========================================================================
// 3. ChargingHalo — wraps a unit row to indicate active charging.
// =========================================================================
export function ChargingHalo({ active = true, children, className = "" }) {
  if (!active) return <>{children}</>;
  return (
    <span
      className={className}
      style={{ position: "relative", display: "block", isolation: "isolate" }}
    >
      <span
        aria-hidden="true"
        style={{
          position: "absolute", inset: -2, borderRadius: "inherit",
          background:
            "radial-gradient(60% 80% at 50% 50%, rgba(46,86,148,0.20), transparent 70%)",
          animation: "chargingPulse 2.2s ease-in-out infinite",
          pointerEvents: "none",
          zIndex: 0,
        }}
      />
      <span style={{ position: "relative", zIndex: 1, display: "block" }}>{children}</span>
      <style>{`
        @keyframes chargingPulse {
          0%, 100% { opacity: 0.55; transform: scale(1); }
          50%      { opacity: 1;    transform: scale(1.02); }
        }
        @media (prefers-reduced-motion: reduce) {
          [style*="chargingPulse"] { animation: none !important; }
        }
      `}</style>
    </span>
  );
}

// =========================================================================
// 4. FleetHealthRing — donut chart that animates from empty on mount.
// =========================================================================
export function FleetHealthRing({
  buckets = { healthy: 0, low: 0, critical: 0 },
  size = 220,
  thickness = 18,
  label = "Fleet health",
}) {
  const total = Math.max(1, buckets.healthy + buckets.low + buckets.critical);
  const pctHealthy  = buckets.healthy  / total;
  const pctLow      = buckets.low      / total;
  const pctCritical = buckets.critical / total;

  const radius = (size - thickness) / 2;
  const circumference = 2 * Math.PI * radius;
  const cx = size / 2;
  const cy = size / 2;

  const [animated, setAnimated] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setAnimated(true), 80);
    return () => clearTimeout(t);
  }, []);

  // Each arc length = pct × circumference. Offset chains them.
  const lenH = pctHealthy  * circumference;
  const lenL = pctLow      * circumference;
  const lenC = pctCritical * circumference;
  const startH = 0;
  const startL = startH + lenH;
  const startC = startL + lenL;

  function arc(start, len, color) {
    return (
      <circle
        cx={cx} cy={cy} r={radius}
        fill="none" stroke={color} strokeWidth={thickness}
        strokeLinecap="butt"
        strokeDasharray={`${animated ? len : 0} ${circumference}`}
        strokeDashoffset={-start}
        style={{ transition: "stroke-dasharray 1100ms cubic-bezier(0.16,1,0.3,1)" }}
        transform={`rotate(-90 ${cx} ${cy})`}
      />
    );
  }

  const headlineNum = buckets.healthy;
  const headlinePct = Math.round(pctHealthy * 100);

  return (
    <div style={{ display: "inline-flex", flexDirection: "column", alignItems: "center", gap: 10 }}>
      <svg width={size} height={size} role="img" aria-label={`${label}: ${headlinePct}% healthy`}>
        {/* track */}
        <circle cx={cx} cy={cy} r={radius} fill="none" stroke="var(--bg-deep)" strokeWidth={thickness}/>
        {arc(startH, lenH, "var(--ok)")}
        {arc(startL, lenL, "var(--warn)")}
        {arc(startC, lenC, "var(--danger)")}
        <text x={cx} y={cy - 6} textAnchor="middle"
          style={{ font: "500 44px 'Crimson Pro', serif", fill: "var(--text)", letterSpacing: "-0.04em" }}>
          {headlinePct}<tspan style={{ fontSize: 20, fill: "var(--text-muted)" }}>%</tspan>
        </text>
        <text x={cx} y={cy + 18} textAnchor="middle"
          style={{ font: "600 10.5px Inter, sans-serif", fill: "var(--text-dim)", letterSpacing: "0.22em", textTransform: "uppercase" }}>
          HEALTHY
        </text>
      </svg>
      <div style={{ display: "flex", gap: 18, fontSize: 12.5 }}>
        <Legend dot="var(--ok)"     n={buckets.healthy}  lbl="healthy"/>
        <Legend dot="var(--warn)"   n={buckets.low}      lbl="low"/>
        <Legend dot="var(--danger)" n={buckets.critical} lbl="critical"/>
      </div>
    </div>
  );
}
function Legend({ dot, n, lbl }) {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6, color: "var(--text-muted)" }}>
      <span style={{ width: 8, height: 8, borderRadius: "50%", background: dot }}/>
      <b style={{ color: "var(--text)", fontWeight: 600 }}>{n}</b> {lbl}
    </span>
  );
}

// =========================================================================
// 5. LiveTick — number that morphs smoothly when value changes.
// =========================================================================
export function LiveTick({ value, unit, precision = 0, style }) {
  const [display, setDisplay] = useState(value);
  const [transitioning, setTransitioning] = useState(false);
  const prevRef = useRef(value);

  useEffect(() => {
    if (value === prevRef.current) return;
    setTransitioning(true);
    const t = setTimeout(() => {
      setDisplay(value);
      prevRef.current = value;
      setTransitioning(false);
    }, 140);
    return () => clearTimeout(t);
  }, [value]);

  const formatted =
    typeof display === "number" && !Number.isNaN(display)
      ? display.toFixed(precision)
      : "—";
  return (
    <span style={{
      display: "inline-block",
      transition: "opacity 140ms ease-out, transform 140ms ease-out",
      opacity: transitioning ? 0 : 1,
      transform: transitioning ? "translateY(-3px)" : "none",
      fontVariantNumeric: "tabular-nums",
      ...style,
    }}>
      {formatted}{unit ? <span style={{ opacity: 0.6, marginLeft: 2 }}>{unit}</span> : null}
    </span>
  );
}
